import "zod/compile";
import { z } from "zod";
import { ExtensionColumnSelectionSchema } from "./schemaExtensions";

export const ColumnConfigSourceSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("base") }),
  z.strictObject({
    kind: z.literal("extension"),
    extensionId: ExtensionColumnSelectionSchema.shape.extensionId,
    /** Immutable schema field ID, not its label or physical column name. */
    field: ExtensionColumnSelectionSchema.shape.fieldId,
    cell: z.enum(["default", "view"]),
    relation: ExtensionColumnSelectionSchema.shape.relation,
  }),
]);

export type ColumnConfigSource = z.infer<typeof ColumnConfigSourceSchema>;

export const COLUMN_CONFIG_FORMATS = [
  "string",
  "number",
  "integer",
  "float",
  "currency",
  "currency4",
  "date",
] as const;

/**
 * How a column's cells are drawn. Every option is optional and absent means
 * the plain look, so a new option (color, emphasis) needs no migration of
 * saved views.
 */
export const ColumnStyleSchema = z.object({
  bold: z.boolean().optional(),
});

export type ColumnStyle = z.infer<typeof ColumnStyleSchema>;

const PLAIN_COLUMN_STYLE: Required<ColumnStyle> = { bold: false };

/** A column's style with every option filled in from the plain look. */
export function columnStyleOf(column: {
  style?: ColumnStyle;
}): Required<ColumnStyle> {
  return { ...PLAIN_COLUMN_STYLE, ...column.style };
}

/**
 * A single column configuration entry stored per-user per-table (and as
 * org/task-page defaults). Used by both backend (Sequelize JSONB) and frontend
 * (column config hook).
 */
export const ColumnConfigEntrySchema = z
  .object({
    /** Unique identifier for this column instance */
    id: z.string().min(1),
    /** Display header shown in the table */
    header: z.string().min(1),
    /** The data field name to read from the row object (empty for built-in columns) */
    apiField: z.string(),
    /** Missing source preserves legacy base-field behavior without rewriting JSON. */
    source: ColumnConfigSourceSchema.optional(),
    /** Whether the column is right-aligned (for numbers/currency) */
    alignRight: z.boolean().optional(),
    /** Whether the column supports backend sorting */
    sortable: z.boolean().optional(),
    /** Whether the column appears as a filter dimension (MultiSelect dropdown) */
    filterable: z.boolean().optional(),
    /** Whether the column is hidden from the table but still drives filters */
    hidden: z.boolean().optional(),
    /** Display/sort format hint: determines how values are cast for sorting and rendered */
    format: z.enum(COLUMN_CONFIG_FORMATS).optional(),
    /**
     * For a `date` column: "medium" writes the month out ("Oct 16, 2026").
     * Absent keeps the short, locale form, so existing columns are unchanged.
     */
    dateStyle: z.enum(["medium"]).optional(),
    /** Width in pixels the user dragged the column to; absent means auto */
    width: z.number().int().positive().optional(),
    /** Cell formatting (bold, ...); absent draws the column plain */
    style: ColumnStyleSchema.optional(),
  })
  .refine(
    (column) => column.source?.kind !== "extension" || column.apiField === "",
    {
      message: "Extension columns use source instead of apiField",
      path: ["apiField"],
    },
  );

export type ColumnConfigEntry = z.infer<typeof ColumnConfigEntrySchema>;

/** One level of an ordered multi-column sort, by column config `id`. */
export const ColumnSortEntrySchema = z.object({
  id: z.string().min(1),
  desc: z.boolean(),
});

export type ColumnSortEntry = z.infer<typeof ColumnSortEntrySchema>;

/** Sort levels in priority order; each level only orders rows the earlier levels left tied. */
export const ColumnSortingSchema = z
  .array(ColumnSortEntrySchema)
  .max(10)
  .refine(
    (sorting) => new Set(sorting.map((s) => s.id)).size === sorting.length,
    {
      message: "A column can appear once in a sort",
    },
  );

export type ColumnSorting = z.infer<typeof ColumnSortingSchema>;
